import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Box,
  IconButton,
  Typography,
  CircularProgress,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { useSelector } from "react-redux";
import { urls } from "../../urls/urls";
import { BASE_URL_TEST } from "../../utils/baseUrl";

export default function CommissionEditModal({ open, onClose, commissionData, onSuccess }) {
  const apptoken = useSelector((state) => state.user.apptoken);
  const [loading, setLoading] = useState(false);
  const [status, setStatus] = useState("pending");

  useEffect(() => {
    if (commissionData) {
      setStatus(commissionData.status || "pending");
    }
  }, [commissionData]);

  const handleStatusChange = (event) => {
    setStatus(event.target.value);
  };

  const getAgentName = () => {
    if (!commissionData?.agentDetails) return "N/A";
    const firstName = commissionData.agentDetails.first_name || "";
    const lastName = commissionData.agentDetails.last_name || "";
    return `${firstName} ${lastName}`.trim() || "N/A";
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: "INR",
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value);
  };

  const handleSubmit = async () => {
    if (!commissionData?.payment_id) {
      console.error("Payment ID is missing");
      return;
    }

    setLoading(true);

    try {
      const response = await fetch(
        BASE_URL_TEST + urls("updateAgentWithdrawStatus"),
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: apptoken ? `Bearer ${apptoken}` : "",
          },
          body: JSON.stringify({
            payment_id: commissionData.payment_id,
            status: status,
          }),
        }
      );

      const result = await response.json();

      if (response.ok && result.status) {
        console.log("Commission updated successfully:", result);
        if (onSuccess) {
          onSuccess();
        }
        onClose();
      } else {
        console.error("Failed to update commission:", result.message || "Unknown error");
      }
    } catch (error) {
      console.error("Error updating commission:", error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: "12px",
        },
      }}
    >
      <DialogTitle
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          bgcolor: "#f5f5f5",
        }}
      >
        <Typography variant="h6" sx={{ fontWeight: "bold" }}>
          Edit Agent Commission Details
        </Typography>
        <IconButton onClick={onClose}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ mt: 2 }}>
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: 2,
            mt: 1,
          }}
        >
          <TextField
            label="Payment ID"
            value={commissionData?.payment_id || ""}
            fullWidth
            disabled
            variant="outlined"
          />

          <TextField
            label="Agent ID"
            value={commissionData?.agent_id || ""}
            fullWidth
            disabled
            variant="outlined"
          />

          <TextField
            label="Agent Name"
            value={getAgentName()}
            fullWidth
            disabled
            variant="outlined"
          />

          <TextField
            label="Email"
            value={commissionData?.agentDetails?.email || ""}
            fullWidth
            disabled
            variant="outlined"
          />

          <TextField
            label="Phone Number"
            value={commissionData?.agentDetails?.phone_number || ""}
            fullWidth
            disabled
            variant="outlined"
          />

          <TextField
            label="Amount"
            value={commissionData?.amount ? formatCurrency(parseFloat(commissionData.amount)) : ""}
            fullWidth
            disabled
            variant="outlined"
          />

          <TextField
            label="Payment Method"
            value={commissionData?.payment_method || "N/A"}
            fullWidth
            disabled
            variant="outlined"
          />

          <TextField
            label="Description"
            value={commissionData?.description || "N/A"}
            fullWidth
            disabled
            variant="outlined"
            multiline
            rows={2}
          />

          <FormControl fullWidth>
            <InputLabel>Status</InputLabel>
            <Select
              value={status}
              onChange={handleStatusChange}
              label="Status"
            >
              <MenuItem value="pending">Pending</MenuItem>
              <MenuItem value="completed">Completed</MenuItem>
              <MenuItem value="failed">Failed</MenuItem>
            </Select>
          </FormControl>
        </Box>
      </DialogContent>

      <DialogActions sx={{ p: 2, bgcolor: "#f5f5f5" }}>
        <Button
          onClick={onClose}
          variant="outlined"
          disabled={loading}
          sx={{
            textTransform: "none",
            borderRadius: "8px",
            color: "#666",
            borderColor: "#ccc",
          }}
        >
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={loading}
          sx={{
            textTransform: "none",
            borderRadius: "8px",
            bgcolor: "#2c666e",
            "&:hover": {
              bgcolor: "#245757",
            },
          }}
        >
          {loading ? <CircularProgress size={24} color="inherit" /> : "Save Changes"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
